import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCoverflow, Navigation, Autoplay } from "swiper/modules";
import { motion, AnimatePresence } from "framer-motion";
import "swiper/css";
import "swiper/css/effect-coverflow";
import "swiper/css/navigation";




// Featured projects
const featuredProjects = [
  { title: "الشركة الوطنية للرمال السوداء", "image": "/assets/images/gallary/black_dust.webp", thumb: "/assets/images/gallary/black_dust.webp", description: "مشروع استراتيجي لاستغلال الثروات المعدنية والرمال السوداء" },
  { title: "الشركة الوطنية لإنشاء وتنمية الطرق", image: "/assets/images/gallary/Roads.webp", thumb: "/assets/images/gallary/Roads.webp", description: "تطوير شبكة الطرق الوطنية لربط المدن والمحافظات" },
  { title: "الشركة الوطنية لبيع المنتجات البترولية", image: "/assets/images/gallary/watanya.webp", thumb: "/assets/images/gallary/watanya.webp", description: "تأمين احتياجات الطاقة وتوزيع المنتجات البترولية" },
  { title: "الشركة الوطنية للمقاولات والتطوير", image: "/assets/images/gallary/construction.webp", thumb: "/assets/images/gallary/construction.webp", description: "تنفيذ المشروعات القومية العملاقة والإنشاءات المتطورة" },
  { title: "أفريكا ستركشرز للخيام", image: "/assets/images/gallary/covers.webp", thumb: "/assets/images/gallary/covers.webp", description: "تصنيع الخيام والأغطية التقنية للاستخدامات المتعددة" },
  { title: "مجموعة حديد المصريين", image: "/assets/images/gallary/egyption_iron.webp", thumb: "/assets/images/gallary/egyption_iron.webp", description: "الريادة في صناعة الحديد والصلب والمنتجات المعدنية" },
];

// Dynamic projects 1-35
const generateDynamicProjects = () => {
  const dynamic = [];
  for (let i = 1; i <= 35; i++) {
    dynamic.push({
      title: `صور من مشاريعنا ${i}`,
      image: `/assets/images/gallary/${i}.webp`,
      thumb: `/assets/images/gallary/thumbs/${i}.webp`, // نسخة صغيرة للعرض السريع
      description: "جهاز مشروعات الخدمة الوطنية",
    });
  }
  return dynamic;
};

export default function NationalProjectsGallery() {
  const [fullscreenIndex, setFullscreenIndex] = useState(null);
  const [isAutoplay, setIsAutoplay] = useState(true);
  const [loadingFullscreen, setLoadingFullscreen] = useState(true);
  const [loadedImages, setLoadedImages] = useState(new Set());

  // Merge projects
  const allProjects = useMemo(() => [...featuredProjects, ...generateDynamicProjects()], []);
  const total = allProjects.length;

  // Navigation
  const goNext = useCallback(() => setFullscreenIndex(prev => prev === null ? 0 : (prev + 1) % total), [total]);
  const goPrev = useCallback(() => setFullscreenIndex(prev => prev === null ? total - 1 : (prev - 1 + total) % total), [total]);

  // Keyboard nav
  useEffect(() => {
    const handler = (e) => {
      if (fullscreenIndex === null) return;
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "Escape") setFullscreenIndex(null);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [fullscreenIndex, goNext, goPrev]);

  const openFullscreen = (idx) => {
    setFullscreenIndex(idx);
    setIsAutoplay(false);
    setLoadingFullscreen(true);
  };

  // Mark image as loaded
  const markImageAsLoaded = useCallback((imageSrc) => {
    setLoadedImages(prev => new Set([...prev, imageSrc]));
  }, []);

  return (
    <div className="relative w-full min-h-screen bg-white overflow-hidden py-12">
      {/* Header */}
      <div className="relative z-10 text-center mb-12 px-6">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full border border-gray-200 mb-4">
          <div className="w-1.5 h-1.5 bg-green-500 rounded-full" />
          <span className="text-sm font-medium text-gray-700">المشروعات الوطنية</span>
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">معرض الإنجازات</h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          اكتشف أبرز المشروعات الوطنية التي تساهم في بناء مستقبل أفضل للوطن
        </p>
      </div>

      {/* Swiper gallery */}
      <div className="relative z-10">
        {fullscreenIndex === null && (
          <Swiper
            effect="coverflow"
            centeredSlides
            slidesPerView="auto"
            preloadImages={true} // preload سريع
            coverflowEffect={{ rotate: 0, stretch: 0, depth: 160, modifier: 1.4, slideShadows: false }}
            navigation={{ nextEl: ".swiper-button-next", prevEl: ".swiper-button-prev" }}
            autoplay={isAutoplay ? { delay: 3500, disableOnInteraction: false } : false}
            speed={650}
            modules={[EffectCoverflow, Navigation, Autoplay]}
            className="!pb-16 !pt-8"
          >
            {allProjects.map((project, index) => (
              <SwiperSlide key={index} className="w-[340px] md:w-[420px] h-[520px] cursor-pointer select-none">
                <ProjectCard 
                  project={project} 
                  onClick={() => openFullscreen(index)}
                  isImageLoaded={loadedImages.has(project.thumb)}
                  onImageLoad={() => markImageAsLoaded(project.thumb)}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        )}

        {/* Prev / Next */}
        <button aria-label="Previous slide" className="swiper-button-prev absolute left-4 top-1/2 z-20 w-12 h-12 rounded-full bg-white border border-gray-200 shadow-md hover:scale-105 transform -translate-y-1/2 flex items-center justify-center">
          <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button aria-label="Next slide" className="swiper-button-next absolute right-4 top-1/2 z-20 w-12 h-12 rounded-full bg-white border border-gray-200 shadow-md hover:scale-105 transform -translate-y-1/2 flex items-center justify-center">
          <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Fullscreen */}
      <AnimatePresence>
        {fullscreenIndex !== null && (
          <motion.div
            key="fullscreen"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/80"
            onClick={() => setFullscreenIndex(null)}
            aria-modal="true"
            role="dialog"
          >
            <div className="relative max-w-[92vw] max-h-[88vh] w-full" onClick={(e) => e.stopPropagation()}>
              {/* Close */}
              <button onClick={() => setFullscreenIndex(null)} aria-label="Close viewer" className="absolute top-4 right-4 z-30 w-11 h-11 rounded-lg bg-white/90 text-gray-700 shadow">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Prev / Next */}
              <button onClick={goPrev} aria-label="Previous image" className="absolute left-3 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full bg-white/90 text-gray-700 shadow">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button onClick={goNext} aria-label="Next image" className="absolute right-3 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full bg-white/90 text-gray-700 shadow">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {/* Counter */}
              <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30 bg-white/90 px-3 py-1 rounded-full text-xs font-medium text-gray-800 shadow">
                {fullscreenIndex + 1} / {total}
              </div>

              {/* Image with spinner */}
              <div className="w-full h-full flex items-center justify-center relative">
                {loadingFullscreen && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-lg">
                    <div className="w-16 h-16 border-4 border-gray-300 border-t-gray-500 rounded-full animate-spin"></div>
                  </div>
                )}
                <img
                  src={allProjects[fullscreenIndex].image}
                  alt={allProjects[fullscreenIndex].title}
                  className={`max-h-[80vh] max-w-full object-contain rounded-lg shadow-2xl bg-white transition-opacity duration-300 ${loadingFullscreen ? "opacity-0" : "opacity-100"}`}
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                  onLoad={() => setLoadingFullscreen(false)}
                />
              </div>

              {/* Info panel */}
              <div className="mt-4 bg-white rounded-lg p-4 shadow-md border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900">{allProjects[fullscreenIndex].title}</h3>
                <p className="text-sm text-gray-600 mt-1">{allProjects[fullscreenIndex].description}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hint */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-white border border-gray-200 rounded-full px-4 py-2 text-gray-700 text-sm shadow">
        اسحب أو اضغط على الصورة لفتح العرض الكامل — استخدم الأسهم أو Esc للخروج
      </div>
    </div>
  );
}

// Reusable card with optimized loader
function ProjectCard({ project, onClick, isImageLoaded, onImageLoad }) {
  const [loading, setLoading] = useState(!isImageLoaded);
  const imgRef = useRef(null);

  useEffect(() => {
    if (isImageLoaded && imgRef.current) {
      setLoading(false);
      imgRef.current.style.opacity = '1';
    }
  }, [isImageLoaded]);

  const handleImageLoad = () => {
    setLoading(false);
    onImageLoad();
  };

  return (
    <div className="h-full bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-md hover:shadow-xl transition-shadow duration-300 cursor-pointer" onClick={onClick}>
      <div className="h-[72%] w-full overflow-hidden bg-gray-100 relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <div className="w-10 h-10 border-4 border-gray-300 border-t-gray-500 rounded-full animate-spin"></div>
          </div>
        )}
        <img
          ref={imgRef}
          src={project.thumb} // استخدام thumbnail لتحميل سريع
          alt={project.title}
          className={`w-full h-full object-cover transition-opacity duration-200 ease-out ${loading ? "opacity-0" : "opacity-100"}`}
          draggable={false}
          onDragStart={(e) => e.preventDefault()}
          onLoad={handleImageLoad}
        />
      </div>
      <div className="p-5 h-[28%] flex flex-col justify-center">
        <h3 className="text-lg md:text-xl font-semibold text-gray-900 leading-tight">{project.title}</h3>
        <p className="text-sm text-gray-600 mt-2 line-clamp-2">{project.description}</p>
      </div>
    </div>
  );
}
